import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Building2,
  CheckCircle2,
  HandHeart,
  Heart,
  Landmark,
  Leaf,
  Scale,
  Sparkles,
  Trophy,
  Users,
} from 'lucide-react';

import { cn } from '@/lib/utils';

type AudienceId = 'individual' | 'corporate' | 'nonprofit' | 'organization';

interface Audience {
  id: AudienceId;
  icon: typeof Users;
  label: { en: string; ar: string };
  tagline: { en: string; ar: string };
  fitNote: { en: string; ar: string };
  benefits: { en: string; ar: string }[];
  csrPillars?: { icon: typeof Leaf; label: { en: string; ar: string } }[];
}

const AUDIENCES: Audience[] = [
  {
    id: 'individual',
    icon: HandHeart,
    label: { en: 'Individual', ar: 'فرد' },
    tagline: {
      en: 'Sponsor a coach in your name',
      ar: 'ارعَ مدرّباً باسمك',
    },
    fitNote: {
      en: 'Best for personal philanthropy, family memorials, milestone gifts. Single-scholarship up.',
      ar: 'مناسب للعطاء الشخصي وتكريم الذكريات والمناسبات. تبدأ من منحة واحدة.',
    },
    benefits: [
      { en: 'Name printed on the certificate of the coach you sponsor', ar: 'اسمك على شهادة المدرب الذي ترعاه' },
      { en: 'Personal thank-you letter from the coach', ar: 'رسالة شكر شخصية من المدرب' },
      { en: 'Optional dedication ("In memory of…")', ar: 'إمكانية الإهداء ("في ذكرى…")' },
      { en: 'Tax-acknowledgement letter where applicable', ar: 'إفادة تبرّع للجهات الضريبية حيث ينطبق' },
    ],
  },
  {
    id: 'corporate',
    icon: Building2,
    label: { en: 'Corporate', ar: 'شركات' },
    tagline: {
      en: 'Anchor a measurable CSR pillar',
      ar: 'ركيزة قابلة للقياس في مسؤوليتكم الاجتماعية',
    },
    fitNote: {
      en: 'Best for companies needing reportable impact: education, sport, conflict-recovery, regional reach.',
      ar: 'مناسب للشركات التي تبحث عن أثر موثَّق وقابل للنشر: تعليم، رياضة، إعادة إعمار، انتشار إقليمي.',
    },
    benefits: [
      { en: 'Logo on every certificate issued under your tier', ar: 'شعار الشركة على جميع الشهادات في حزمتكم' },
      { en: 'Joint launch press release + impact report', ar: 'بيان إطلاق مشترك + تقرير أثر مفصَّل' },
      { en: 'Speaker slot at the live closing ceremony', ar: 'كلمة مباشرة في الحفل الختامي' },
      { en: 'Documented metrics aligned with UN SDG 4 + 16', ar: 'مؤشرات موثّقة بمحاذاة أهداف التنمية المستدامة 4 و16' },
      { en: 'Co-branded social campaign during the 70-day execution', ar: 'حملة سوشيال مشتركة عبر المراحل الـ70 يوماً' },
    ],
    csrPillars: [
      { icon: Leaf, label: { en: 'Education access (SDG 4)', ar: 'إتاحة التعليم (SDG 4)' } },
      { icon: Scale, label: { en: 'Conflict recovery (SDG 16)', ar: 'التعافي من النزاع (SDG 16)' } },
      { icon: Trophy, label: { en: 'Sport for development', ar: 'الرياضة من أجل التنمية' } },
    ],
  },
  {
    id: 'nonprofit',
    icon: Heart,
    label: { en: 'Non-profit / NGO', ar: 'منظمة غير ربحية' },
    tagline: {
      en: 'Co-deliver, co-credit',
      ar: 'تنفيذ مشترك واعتراف مشترك',
    },
    fitNote: {
      en: 'Best for foundations, humanitarian NGOs, sports-for-peace organisations seeking on-ground delivery via Al-Jalaa.',
      ar: 'مناسب للمؤسسات الخيرية والمنظمات الإنسانية ومنظمات الرياضة من أجل السلام التي تبحث عن تنفيذ ميداني عبر نادي الجلاء.',
    },
    benefits: [
      { en: 'Co-host status alongside Al-Jalaa Sports Club', ar: 'صفة شريك تنفيذ مع نادي الجلاء' },
      { en: 'Joint reporting suitable for donors and grants', ar: 'تقارير مشتركة جاهزة للممولين والمنح' },
      { en: 'Participation in the 70-day execution committee', ar: 'مشاركة في لجنة التنفيذ خلال الـ70 يوماً' },
      { en: 'Shared visibility on the impact report cover', ar: 'ظهور مشترك على غلاف تقرير الأثر' },
    ],
  },
  {
    id: 'organization',
    icon: Landmark,
    label: { en: 'Federation / Government', ar: 'اتحاد / جهة حكومية' },
    tagline: {
      en: 'Institutional partnership, formal mandate',
      ar: 'شراكة مؤسّسية بصلاحيات رسمية',
    },
    fitNote: {
      en: 'Best for football federations, ministries, regional bodies (FIFA, AFC, UEFA partners, GCC sports councils).',
      ar: 'مناسب للاتحادات الرياضية والوزارات والهيئات الإقليمية (شركاء FIFA و AFC و UEFA ومجالس الرياضة الخليجية).',
    },
    benefits: [
      { en: 'Formal MOU framework with Champions Academies + PFA-aligned delivery', ar: 'إطار مذكرة تفاهم رسمي مع تشامبيونز أكاديمي بمحاذاة الاتحاد الفلسطيني' },
      { en: 'Strategic seat on Phase 02 planning committee', ar: 'مقعد استراتيجي في لجنة المرحلة الثانية' },
      { en: 'Submission package for FIFA Forward / AFC Vision Asia eligibility', ar: 'ملف تقديم متوافق مع FIFA Forward و AFC Vision Asia' },
      { en: 'Custom delegation visit + executive briefing', ar: 'زيارة وفد مخصّصة + إحاطة تنفيذية' },
    ],
  },
];

interface PartnerLogo {
  id: string;
  name: string;
  src?: string;
  textBadge?: string;
}

const PARTNER_LOGOS: PartnerLogo[] = [
  { id: 'champions-academies', name: 'Champions Academies', textBadge: 'CHAMPIONS ACADEMIES' },
  { id: 'champions-hub', name: 'Champions Hub', src: '/logos/champions-hub-horizontal-light.png' },
  { id: 'al-jalaa', name: 'Al-Jalaa Sports Club', textBadge: 'AL-JALAA' },
  { id: 'bihub', name: 'Barça Innovation Hub', textBadge: 'BiHUB' },
  { id: 'ac-football-center', name: 'AC Football Center', src: '/logos/ac-football-center.png' },
  { id: 'ekkono', name: 'Ekkono Coaches Academy', src: '/logos/ekkono.png' },
  { id: 'pfa', name: 'Palestinian Football Association', textBadge: 'PFA' },
  { id: 'fifa', name: 'FIFA — engaging', textBadge: 'FIFA' },
  { id: 'afc', name: 'AFC — engaging', textBadge: 'AFC' },
  { id: 'unrwa', name: 'UNRWA — coordination', textBadge: 'UNRWA' },
];

export function SponsorAudiences() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang = isRTL ? 'ar' : 'en';

  const [active, setActive] = useState<AudienceId>('corporate');
  const current = AUDIENCES.find((a) => a.id === active)!;
  const ActiveIcon = current.icon;

  return (
    <section
      id="audiences"
      className="relative py-24 lg:py-32 bg-background overflow-hidden scroll-mt-20"
    >
      <img
        aria-hidden
        src="/img/initiative/audiences-sponsor.webp"
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-40 pointer-events-none"
        loading="lazy"
      />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-background/85 via-background/70 to-background pointer-events-none" />
      <div className="absolute inset-0 bg-dot-grid opacity-30 pointer-events-none" />
      <motion.div
        aria-hidden
        animate={{ scale: [1, 1.12, 1], opacity: [0.12, 0.28, 0.12] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -top-24 -left-32 w-[36rem] h-[36rem] bg-accent/10 rounded-full blur-3xl"
      />

      <div className="container mx-auto px-4 relative">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-6 mb-12 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-8"
          >
            <div className="section-mark text-accent">
              ※ {isRTL ? 'مَن يستطيع الرعاية' : 'Who can sponsor'}
            </div>
            <h2
              className={cn(
                'mt-4 font-display font-black uppercase leading-[0.95] text-balance',
                'text-5xl lg:text-6xl xl:text-7xl max-w-4xl',
                isRTL && 'font-display-ar normal-case leading-[1.1]'
              )}
            >
              {isRTL ? (
                <>
                  أربع مسارات.{' '}
                  <span className="bg-gradient-to-r from-accent to-accent/60 bg-clip-text text-transparent">
                    أثر واحد.
                  </span>
                </>
              ) : (
                <>
                  Four paths.{' '}
                  <span className="bg-gradient-to-r from-accent to-accent/60 bg-clip-text text-transparent">
                    One impact.
                  </span>
                </>
              )}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="lg:col-span-4 font-editorial-italic text-base lg:text-lg text-foreground/70 leading-relaxed"
          >
            "{isRTL
              ? 'الرعاية ليست محصورة بالشركات الكبرى. كل مسار له شروطه وحقوقه — اختر المسار الذي يعكس مؤسستك.'
              : 'Sponsorship is not just for corporations. Each path has its own scope and entitlements — pick the one that reflects your institution.'}"
          </motion.p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {AUDIENCES.map((a) => {
            const Icon = a.icon;
            const isActive = a.id === active;
            return (
              <motion.button
                key={a.id}
                type="button"
                onClick={() => setActive(a.id)}
                whileTap={{ scale: 0.97 }}
                className={cn(
                  'inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 font-mono-mark text-[11px] font-bold uppercase tracking-widest transition-all',
                  isActive
                    ? 'border-accent bg-accent/10 text-accent shadow-lg shadow-accent/15'
                    : 'border-border/60 text-foreground/70 hover:border-accent/50 hover:text-foreground'
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {a.label[lang]}
              </motion.button>
            );
          })}
        </div>

        {/* Active audience panel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
            className="grid lg:grid-cols-12 gap-6 lg:gap-7"
          >
            {/* Left: tagline + benefits */}
            <div className="lg:col-span-7 p-7 lg:p-9 rounded-3xl border-2 border-accent/40 bg-surface-1/70 backdrop-blur-xl">
              <div className="flex items-start gap-4 mb-5">
                <div className="h-12 w-12 rounded-xl bg-accent/15 border border-accent/40 flex items-center justify-center flex-shrink-0">
                  <ActiveIcon className="h-5 w-5 text-accent" />
                </div>
                <div className="min-w-0">
                  <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-1">
                    {current.label[lang]}
                  </div>
                  <h3
                    className={cn(
                      'font-display font-black uppercase leading-tight text-foreground',
                      'text-2xl sm:text-3xl lg:text-4xl',
                      isRTL && 'font-display-ar normal-case leading-[1.15]'
                    )}
                  >
                    {current.tagline[lang]}
                  </h3>
                </div>
              </div>
              <p className="font-editorial-italic text-base text-foreground/75 leading-relaxed mb-6">
                "{current.fitNote[lang]}"
              </p>
              <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-3">
                ※ {isRTL ? 'ما تحصل عليه' : "What you get"}
              </div>
              <ul className="space-y-3">
                {current.benefits.map((b, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: isRTL ? 12 : -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.35,
                      delay: 0.05 + Math.min(i * 0.06, 0.3),
                    }}
                    className="flex gap-3 text-sm leading-snug"
                  >
                    <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-accent" strokeWidth={2.4} />
                    <span className="text-foreground/85">{b[lang]}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Right: meta — CSR pillars (corporate only) or generic info */}
            <div className="lg:col-span-5 space-y-5">
              {current.csrPillars && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  className="p-6 lg:p-7 rounded-2xl border-2 border-border/60 bg-surface-1/60 backdrop-blur-xl"
                >
                  <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-1">
                    {isRTL ? 'مسؤولية اجتماعية' : 'Social responsibility'}
                  </div>
                  <h4 className="font-display font-black uppercase text-xl lg:text-2xl leading-tight text-foreground mb-4">
                    {isRTL ? 'ركائز CSR قابلة للقياس' : 'Measurable CSR pillars'}
                  </h4>
                  <ul className="space-y-3">
                    {current.csrPillars.map((p, i) => {
                      const PIcon = p.icon;
                      return (
                        <li
                          key={i}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-surface-2/60 border border-border/40"
                        >
                          <div className="h-7 w-7 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                            <PIcon className="h-3.5 w-3.5 text-accent" />
                          </div>
                          <span className="text-sm text-foreground/90">
                            {p.label[lang]}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="mt-4 text-[11px] font-editorial-italic text-foreground/55 leading-relaxed">
                    {isRTL
                      ? 'مؤشرات منسجمة مع أهداف الأمم المتحدة للتنمية المستدامة (SDG)، جاهزة للإدراج في تقرير الاستدامة السنوي.'
                      : 'Indicators aligned with UN Sustainable Development Goals — ready for inclusion in your annual sustainability report.'}
                  </p>
                </motion.div>
              )}

              {/* Quick scope card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="p-6 lg:p-7 rounded-2xl border-2 border-accent/40 bg-gradient-to-br from-surface-1 via-surface-1 to-accent/5 relative overflow-hidden"
              >
                <Sparkles className="absolute top-5 right-5 h-4 w-4 text-accent/60" />
                <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-2">
                  {isRTL ? 'الخطوة التالية' : 'Next step'}
                </div>
                <p className="font-display font-black uppercase text-lg lg:text-xl leading-tight text-foreground mb-4">
                  {isRTL
                    ? 'استخدم الحاسبة لتصميم حزمتك ثم أرسل النموذج.'
                    : 'Use the calculator to size your fit, then send the form.'}
                </p>
                <a
                  href="#custom-package"
                  className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-accent text-accent-foreground font-bold uppercase tracking-wider text-xs btn-glow"
                >
                  {isRTL ? 'افتح الحاسبة' : 'Open the calculator'}
                </a>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* ─── Partners / sports logos wall ─── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.2 }}
          className="relative mt-16 pt-12 border-t border-border/40"
        >
          <img
            aria-hidden
            src="/img/initiative/logos-wall.webp"
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-30 pointer-events-none rounded-lg"
            loading="lazy"
          />
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
            <div>
              <div className="section-mark text-accent">
                ⚙ {isRTL ? 'الشبكة' : 'The network'}
              </div>
              <h3
                className={cn(
                  'mt-3 font-display font-black uppercase leading-[0.95] text-3xl lg:text-4xl text-foreground',
                  isRTL && 'font-display-ar normal-case leading-[1.1]'
                )}
              >
                {isRTL ? 'شركاء وسلطات تقف خلف المبادرة' : 'Partners & authorities behind the initiative'}
              </h3>
            </div>
            <p className="font-mono-mark text-[10px] uppercase tracking-widest text-foreground/55 max-w-xs">
              {isRTL
                ? 'مزيج من المنفّذين، شركاء التعليم، والاتحادات الدولية المُشاركة أو المستهدفة.'
                : 'Operators, education partners, and federations either active or being engaged.'}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
            {PARTNER_LOGOS.map((logo, i) => (
              <motion.div
                key={logo.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.4,
                  delay: Math.min(i * 0.05, 0.4),
                }}
                whileHover={{ y: -3 }}
                className="group relative aspect-[5/3] flex items-center justify-center rounded-xl border border-border/60 bg-surface-1/60 backdrop-blur-xl px-4 hover:border-accent/40 transition-colors"
              >
                {logo.src ? (
                  <img
                    src={logo.src}
                    alt={logo.name}
                    className="max-h-10 max-w-full w-auto object-contain opacity-80 group-hover:opacity-100 transition-opacity"
                  />
                ) : (
                  <span className="font-display font-black tracking-tight text-base sm:text-lg text-foreground/85 group-hover:text-accent transition-colors">
                    {logo.textBadge}
                  </span>
                )}
                <span className="sr-only">{logo.name}</span>
              </motion.div>
            ))}
          </div>

          <p className="mt-6 text-[11px] font-editorial-italic text-foreground/50 leading-relaxed text-center max-w-2xl mx-auto">
            {isRTL
              ? 'بعض الجهات (FIFA · AFC · UNRWA) في مرحلة التواصل الرسمي وليست رعاة بعد. الظهور هنا يعكس حالة العلاقة وقت النشر.'
              : 'Some entities (FIFA · AFC · UNRWA) are currently in formal-engagement stage, not yet sponsors. Listing reflects the relationship state at publication.'}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
